import React from "react";

import * as R from "ramda";

import PropTypes from "prop-types";

import TextAreaAutoSize from "react-textarea-autosize";

import { branch, renderComponent, withProps, defaultProps } from "recompose";

import styled, { css } from "react-emotion";

import ImgWithPolyfills from "./ImgWithPolyfills";

import { colors, fonts, shevy, themeColor } from "src/styles";

import format from "string-format";

import GatsbyLink from "gatsby-link";

import { setLightness } from "polished";

import {
  flex,
  flexWrap,
  flexDirection,
  alignItems,
  justifyContent,
  alignSelf,
  space,
  width,
  fontSize,
  fontWeight,
  textAlign,
  color,
  responsiveStyle,
  borderColor,
  borderWidth,
  style,
  focus,
  hover,
  theme,
  propTypes,
  cleanElement,
} from "styled-system";

import { baseFontSize } from "src/styles/typography";

const printStyles = ({ printStyles }) => {
  if (printStyles) {
    return printStyles;
  }
};

const responsivePropType = PropTypes.oneOfType([
  PropTypes.number,
  PropTypes.string,
  PropTypes.array,
]);

const borderRadius = responsiveStyle({
  prop: "borderRadius",
  cssProperty: "borderRadius",
  key: "radii",
  numberToPx: true,
});

const borderTopLeftRadius = responsiveStyle({
  prop: "borderTopLeftRadius",
  cssProperty: "borderTopLeftRadius",
  key: "radii",
  numberToPx: true,
});

const borderTopRightRadius = responsiveStyle({
  prop: "borderTopRightRadius",
  cssProperty: "borderTopRightRadius",
  key: "radii",
  numberToPx: true,
});

const borderBottomLeftRadius = responsiveStyle({
  prop: "borderBottomLeftRadius",
  cssProperty: "borderBottomLeftRadius",
  key: "radii",
  numberToPx: true,
});

const borderBottomRightRadius = responsiveStyle({
  prop: "borderBottomRightRadius",
  cssProperty: "borderBottomRightRadius",
  key: "radii",
  numberToPx: true,
});

const boxShadow = responsiveStyle({
  prop: "boxShadow",
  key: "shadows",
  cssProperty: "boxShadow",
});

const display = responsiveStyle({
  prop: "display",
  cssProperty: "display",
});

const position = responsiveStyle({
  prop: "position",
  cssProperty: "position",
});

const top = responsiveStyle({
  prop: "top",
  cssProperty: "top",
  numberToPx: true,
});

const right = responsiveStyle({
  prop: "right",
  cssProperty: "right",
  numberToPx: true,
});

const bottom = responsiveStyle({
  prop: "bottom",
  cssProperty: "bottom",
  numberToPx: true,
});

const left = responsiveStyle({
  prop: "left",
  cssProperty: "left",
  numberToPx: true,
});

const height = responsiveStyle({
  prop: "height",
  cssProperty: "height",
  numberToPx: true,
});

const maxWidth = responsiveStyle({
  prop: "maxWidth",
  cssProperty: "maxWidth",
  numberToPx: true,
});

const minWidth = responsiveStyle({
  prop: "minWidth",
  cssProperty: "minWidth",
  numberToPx: true,
});

export const Box = styled.div`
  ${space};
  ${color};
  ${width};
  ${height};
  ${flex};
  ${borderColor};
  ${borderRadius};
  ${borderTopLeftRadius};
  ${borderTopRightRadius};
  ${borderBottomLeftRadius};
  ${borderBottomRightRadius};
  ${borderWidth};
  ${display};
  ${focus};
  ${hover};
  ${maxWidth};
  ${minWidth};
  ${position};
  ${top};
  ${bottom};
  ${left};
  ${right};
  @media screen {
    ${boxShadow};
  }
  @media print {
    ${printStyles};
  }
`;

export const Flex = styled(Box)`
  display: flex;
  ${flexWrap};
  ${flexDirection};
  ${alignItems};
  ${alignSelf};
  ${justifyContent};
  ${hover};
`;

export const Image = Box.withComponent(ImgWithPolyfills);

export const PlainImage = Box.withComponent("img");

const textDecoration = style({
  prop: "textDecoration",
  cssProperty: "textDecoration",
});

const textTransform = style({
  prop: "textTransform",
  cssProperty: "textTransform",
});

const whiteSpace = style({
  prop: "whiteSpace",
  cssProperty: "whiteSpace",
});

export const Text = styled(Flex)`
  font-family: ${props => (props.numeric ? fonts.numeric : fonts.sansSerif)};
  font-size: ${baseFontSize}px;
  font-weight: ${props => (props.numeric ? "100" : "400")};
  flex-direction: column;
  display: ${props => (props.inline ? "inline" : "flex")};
  ${flexDirection};
  ${fontSize};
  ${fontWeight};
  ${textAlign};
  ${textDecoration};
  ${textTransform};
  ${whiteSpace};
  ${hover};
`;

const buttonStyle = ({ theme }) =>
  css`
    background: ${theme.primaryColor};
    color: ${theme.colors.white};
    border-radius: ${theme.radii[5]}px;
    padding: ${theme.space[2]}px ${theme.space[3]}px;
    font-weight: ${theme.fontWeights.semibold};
    font-size: ${theme.fontSizes[3]}px;
    text-decoration: none;
    display: inline-block;
    :hover {
      background: ${theme.primaryColorHover};
    }
  `;

const _setLightness = (amt, color) =>
  console.warn("color", color) || setLightness(0.9, color);

export const Markup = withProps(({ html, theme, values, fontSize }) => ({
  dangerouslySetInnerHTML: { __html: values ? format(html, values) : html },
  headingColor: themeColor(theme) || colors.text,
}))(styled(Box)`
  font-family: ${fonts.sansSerif};
  p,
  ul,
  ol,
  pre {
    ${shevy.content};
    ${fontSize};
  }
  h1 {
    color: ${theme(`primaryColor`)};
    text-transform: uppercase;
  }
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${theme(`primaryColor`)};
  }
  h1 {
    ${shevy.h1};
  }
  h2 {
    ${shevy.h2};
  }
  h3 {
    ${shevy.h3};
  }
  h4 {
    ${shevy.h4};
  }
  h5 {
    ${shevy.h5};
  }
  h6 {
    ${shevy.h6};
  }
  blockquote {
    margin: 0;
    p {
      ${shevy.h2};
      font-weight: ${theme("fontWeights.bold")};
      margin: ${shevy.baseSpacing(1.5)};
      margin-left: 0;
      margin-right: 0;
      font-style: italic;
      text-transform: uppercase;
    }
    color: ${theme(`colors.orange`)};
  }
  table {
    border-collapse: collapse;
    width: 100%;
    border: 1px solid ${theme(`colors.tableBorder`)};
    border-width: 1px 0;
    thead {
      th {
        background-color: ${theme(`primaryColor`)};
        color: ${theme("colors.white")};
      }
    }
    td,
    th {
      padding: ${theme("space.1")}px;
    }
    tr:nth-child(even) {
      td {
        background-color: ${theme(`primaryColorAlt`)};
      }
    }
  }
  iframe {
    border: none;
  }
  a {
    color: ${props => {
    const linkColor = props.linkColor || "blue";
    return theme(`colors.${linkColor}`)(props);
  }};
  }
  a.button {
    margin: ${theme("space.1")}px 0;
    ${buttonStyle};
  }
  .disclaimer {
    color: ${theme("colors.muted")};
    font-size: ${theme("fontSizes.2")}px;
    font-style: italic;
  }
  img {
    max-width: 100%;
  }
`);

export const H1 = styled(Text)`
  ${shevy.h1};
  text-transform: uppercase;
  font-weight: bold;
`.withComponent("h1");

export const H2 = styled(Text)`
  ${shevy.h2};
  font-weight: bold;
`.withComponent("h2");

export const H3 = styled(Text)`
  ${shevy.h3};
  font-weight: bold;
`.withComponent("h3");

export const H4 = styled(Text)`
  ${shevy.h4};
  font-weight: bold;
`.withComponent("h4");

export const H5 = styled(Text)`
  ${shevy.h5};
  font-weight: bold;
`.withComponent("h5");

export const H6 = styled(Text)`
  ${shevy.h6};
  font-weight: bold;
`.withComponent("h6");

const CleanGatsbyLink = cleanElement(GatsbyLink);

CleanGatsbyLink.propTypes = {
  ...propTypes.textAlign,
  ...propTypes.color,
  ...propTypes.space,
  ...propTypes.justifyContent,
  ...propTypes.alignItems,
  ...propTypes.alignSelf,
  ...propTypes.fontSize,
  ...propTypes.fontWeight,
  ...propTypes.flexDirection,
  ...propTypes.borderWidth,
  ...propTypes.borderColor,
  borderRadius: responsivePropType,
  textTransform: responsivePropType,
  boxShadow: responsivePropType,
};

const PossiblyExternalLink = ({ children, to, ...other }) => {
  // This assumes that any internal link (intended for Gatsby)
  // will start with exactly one slash, and that anything else is external.
  const internal = /^\/(?!\/)/.test(to);

  // Use gatsby-link for internal links, and <a> for others
  if (internal) {
    return (
      <CleanGatsbyLink to={to} {...other}>
        {children}
      </CleanGatsbyLink>
    );
  }
  return (
    <a
      href={to}
      {...R.omit(
        [
          "borderRadius",
          "borderWidth",
          "borderBottom",
          "borderTop",
          "borderColor",
          "activeStyle",
          "textTransform",
          "flexDirection",
        ],
        other
      )}
    >
      {children}
    </a>
  );
};

export const Link = Text.withComponent(PossiblyExternalLink);

export const A = withProps({
  display: "inline",
})(Text.withComponent("a"));

export const FlexLink = Flex.withComponent("a");

export const Button = Flex.withComponent("button");

export const ButtonLink = styled(Link)`
  ${buttonStyle};
`;

export const Label = Text.withComponent("label");

const inputStyle = {
  borderWidth: 1,
  borderColor: "divider",
};

const placeholderStyle = ({ placeholderColor = "placeholder" }) => ({
  "&::placeholder": {
    color: colors[placeholderColor],
  },
});

let Input = withProps(inputStyle)(Text.withComponent("input"));

Input = styled(Input)(placeholderStyle);

export { Input };

let TextAreaFixedSize = withProps(inputStyle)(Text.withComponent("textarea"));

TextAreaFixedSize = styled(TextAreaFixedSize)(placeholderStyle);

const CleanTextAreaAutoSize = cleanElement(TextAreaAutoSize);

// TODO: Can we reconcile this with the CleanGatsbyLink propTypes?
CleanTextAreaAutoSize.propTypes = {
  ...propTypes.textAlign,
  ...propTypes.color,
  ...propTypes.space,
  ...propTypes.justifyContent,
  ...propTypes.alignItems,
  ...propTypes.alignSelf,
  ...propTypes.fontSize,
  ...propTypes.fontWeight,
  ...propTypes.flexDirection,
  borderRadius: responsivePropType,
  borderWidth: responsivePropType,
  borderColor: responsivePropType,
  textTransform: responsivePropType,
  boxShadow: responsivePropType,
  autoSize: PropTypes.bool,
};

let TextAreaWithAutoSize = withProps(inputStyle)(
  Text.withComponent(CleanTextAreaAutoSize)
);

TextAreaWithAutoSize = styled(TextAreaWithAutoSize)(placeholderStyle);

export const TextArea = branch(
  R.prop("autoSize"),
  renderComponent(TextAreaWithAutoSize),
  renderComponent(TextAreaFixedSize)
)();

export const Form = Flex.withComponent("form");

const CardEdge = defaultProps({
  bg: "cardEdge",
  p: 3,
})(Flex);

const CardHeader = withProps({
  borderTopRightRadius: 3,
  borderTopLeftRadius: 3,
})(CardEdge);

const CardFooter = withProps({
  borderBottomRightRadius: 3,
  borderBottomLeftRadius: 3,
})(CardEdge);

export const Card = defaultProps({
  bg: "white",
  borderRadius: 3,
  boxShadow: 2,
})(Box);

Card.Header = CardHeader;
Card.Footer = CardFooter;



// WEBPACK FOOTER //
// ./src/components/base.js